// Generated by tools/generate-project-agreement.mjs; edit tools/contracts/project-agreement.cts.
type DocumentRun = { text: string; bold: boolean };
export type DocumentBlock = { type: string; prefix?: string; runs?: DocumentRun[]; rows?: { span: number; paragraphs: DocumentRun[][] }[][] };
type Blocks = DocumentBlock[];
type AuthorizationScope = { version: 1; values: Record<string, string | string[]> };
export type AgreementConsent = { accepted: true; templateSha256: string; companyName: string; creditCode: string };
import agreement from "./project-agreement.json";

function agreementPayload(value: unknown): string {
  const canonical = (item: unknown): unknown => Array.isArray(item) ? item.map(canonical)
    : item && typeof item === "object" ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b, "en")).map(([key, entry]) => [key, canonical(entry)])) : item;
  return JSON.stringify(canonical(value));
}
function parseAgreementConsent(value: unknown): AgreementConsent | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  if (Object.keys(row).some(key => !["accepted", "templateSha256", "companyName", "creditCode"].includes(key))
    || row.accepted !== true || typeof row.templateSha256 !== "string" || !/^[a-f0-9]{64}$/.test(row.templateSha256)
    || typeof row.companyName !== "string" || !row.companyName.trim() || row.companyName.length > 200
    || typeof row.creditCode !== "string" || !/^[0-9A-Z]{18}$/.test(row.creditCode)) return null;
  return { accepted: true, templateSha256: row.templateSha256, companyName: row.companyName, creditCode: row.creditCode };
}
function personalizeAgreement(blocks: Blocks, parties: { companyName?: string; creditCode?: string; name?: string; idCard?: string; amountMinor?: number }): Blocks {
  return blocks.map(block => {
    if (!block.runs) return block;
    const original = block.runs.map(run => run.text).join("");
    let text = original;
    if (parties.companyName) text = text.replace("【企业全称】", parties.companyName);
    if (parties.name) text = text.replace("【自然人姓名】", parties.name);
    if (parties.idCard && original.startsWith("身份证号：")) text = `身份证号：${parties.idCard}`;
    if (parties.creditCode && original === "统一社会信用代码：【】") text = `统一社会信用代码：${parties.creditCode}`;
    if (parties.amountMinor !== undefined) text = text.replace(/授权费为人民币【[\s　]*】元/u, `授权费为人民币${(parties.amountMinor / 100).toFixed(2)}元`);
    return text === original ? block : { ...block, runs: [{ text, bold: block.runs.some(run => run.bold) }] };
  });
}
function agreementTerm(offer?: { package: { platformPackage?: { months?: number }; durationDays: number } } | null) {
  return offer?.package.platformPackage?.months ? `确认起 ${offer.package.platformPackage.months}个月` : offer ? `确认起 ${offer.package.durationDays}天` : "确认起 1年";
}
// Populate the original appendix table without changing the agreement template.
function fillAgreementAppendix(blocks: Blocks, scope: AuthorizationScope, details?: { usage?: string; term?: string }): Blocks {
  const keys: Record<string, string> = {
    "人像素材编号": "materialId", "项目用途": "purpose", "被许可行为": "actions",
    "授权类型": "licenseType", "授权地域": "region", "媒介与渠道": "channels",
    "行业与题材": "industries", "人物角色、服装与台词": "character", "声音使用": "voice",
    "AI 处理方式": "ai", "数字分身持续调用": "digitalDouble", "技术服务商": "providers", "其他": "other",
  };
  let appendix = false;
  return blocks.map(block => {
    if (block.runs?.map(run => run.text).join("").includes("附件：授权范围")) appendix = true;
    if (!appendix || block.type !== "table" || !block.rows) return block;
    appendix = false;
    const hasExcluded = block.rows.some(row => row[0]?.paragraphs.flat().map(run => run.text).join("").trim() === "排除行业与题材");
    const rows = block.rows.flatMap(row => {
      const label = row[0]?.paragraphs.flat().map(run => run.text).join("").trim();
      return label === "行业与题材" && !hasExcluded ? [row, [
        { span: 1, paragraphs: [[{ text: "排除行业与题材", bold: false }]] },
        { span: 1, paragraphs: [[{ text: "详见《数字形象授权范围说明》", bold: false }]] },
      ]] : [row];
    });
    return { ...block, rows: rows.map(row => {
      const label = row[0]?.paragraphs.flat().map(run => run.text).join("").trim();
      if (label === "排除行业与题材") {
        const excluded = scope.values.excludedIndustries;
        const text = typeof excluded === "string" && excluded !== "未单独约定" && excluded.trim() ? excluded : "详见《数字形象授权范围说明》";
        return row.map((cell, index) => index === 1 ? { ...cell, paragraphs: [[{ text, bold: false }]] } : cell);
      }
      const key = keys[label];
      let value: string | string[] | undefined = label === "授权期限" ? details?.term : key ? scope.values[key] : undefined;
      if (label === "项目用途" && details?.usage) value = [details.usage, typeof value === "string" ? value : ""].filter(Boolean).join(" · ");
      if (label === "授权类型" && value === "独家" && scope.values.exclusiveScope) value = `独家 · ${scope.values.exclusiveScope}`;
      if (label === "AI 处理方式" && Array.isArray(value) && value.includes("其他") && scope.values.aiOther) value = value.map(item => item === "其他" ? `其他：${scope.values.aiOther}` : item);
      const text = Array.isArray(value) ? value.join("、") : value;
      if (row.length < 2 || (!key && label !== "授权期限")) return row;
      if (!text) return row.map((cell, index) => index === 1 ? { ...cell, paragraphs: [[{ text: "未单独约定", bold: false }]] } : cell);
      const categories = label === "行业与题材" ? text.match(/^电商类：([\s\S]*?；)\s*影视类：([\s\S]*)$/u) : null;
      const paragraphs = categories ? [
        [{ text: "电商类：", bold: true }, { text: categories[1], bold: false }],
        [{ text: "影视类：", bold: true }, { text: categories[2], bold: false }],
      ] : [[{ text, bold: false }]];
      return row.map((cell, index) => index === 1 ? { ...cell, paragraphs } : cell);
    }) };
  });
}

export { agreementPayload, agreement, parseAgreementConsent, personalizeAgreement, agreementTerm, fillAgreementAppendix };
